import {
  FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID,
  FREEMIUM_MINI_QUIZ_EXPERIMENT_ID,
  FREEMIUM_REENGAGEMENT_EXPERIMENT_ID,
  isFreemiumTreatment,
} from '../freemium/freemiumTreatment';
import {
  type ExperimentFlagsObject,
  type ExperimentHookOptions,
  type GetVariants,
} from '../types';
import { useExperimentFlags } from './useExperimentFlags';

export type FreemiumTreatment =
  | {
      isLoading: true;
      isTreatment: false;
      miniQuizExp255: null;
      guestFreemiumReengagementExp260: null;
      freemiumCheckoutConversionExp266: null;
    }
  | {
      isLoading: false;
      /** A treatment arm of Exp 255, Exp 260, or Exp 266. */
      isTreatment: boolean;
      miniQuizExp255: ExperimentFlagsObject<
        GetVariants<typeof FREEMIUM_MINI_QUIZ_EXPERIMENT_ID>
      >;
      guestFreemiumReengagementExp260: ExperimentFlagsObject<
        GetVariants<typeof FREEMIUM_REENGAGEMENT_EXPERIMENT_ID>
      >;
      freemiumCheckoutConversionExp266: ExperimentFlagsObject<
        GetVariants<typeof FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID>
      >;
    };

/**
 * The client-side read behind every freemium guest gate: Exp 255, Exp 260,
 * and Exp 266 resolved together, with `isTreatment` computed by the same
 * `isFreemiumTreatment` the SSR seed uses, so hydration agrees with the
 * server HTML.
 *
 * `options.enroll` applies to Exp 255 only — it is what the homepage
 * hamburger flips to enroll an anonymous visitor. Exp 260 and Exp 266 are
 * always non-enrolling peeks here. Exp 260 is backend-only, so an anonymous
 * enrollment attempt could never succeed and would only log
 * `experiments.enrollment.post-signup-experiment`. Exp 266 does enroll
 * client-side, but on one surface only (`/mini-quiz/loading/`, via
 * `useEnrollExp266`), never through this hook — its population must be
 * mini-quiz signups, not everyone who opens the hamburger. The two reads are
 * kept separate for that reason; do not fold them into one
 * `useExperimentFlags` call.
 *
 * For an authenticated customer both reads come from the one
 * `/customers/self/experiments/` query, so they resolve in the same render.
 */
export function useFreemiumTreatment(
  options?: Pick<ExperimentHookOptions, 'enroll'>
): FreemiumTreatment {
  const exp255 = useExperimentFlags(
    [FREEMIUM_MINI_QUIZ_EXPERIMENT_ID],
    options
  );
  const reengagement = useExperimentFlags(
    [
      FREEMIUM_REENGAGEMENT_EXPERIMENT_ID,
      FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID,
    ],
    { enroll: false }
  );

  if (exp255.isLoading || reengagement.isLoading) {
    return {
      isLoading: true,
      isTreatment: false,
      miniQuizExp255: null,
      guestFreemiumReengagementExp260: null,
      freemiumCheckoutConversionExp266: null,
    };
  }

  return {
    isLoading: false,
    isTreatment: isFreemiumTreatment({
      variant255: exp255.miniQuizExp255.variant,
      variant260: reengagement.guestFreemiumReengagementExp260.variant,
      variant266: reengagement.freemiumCheckoutConversionExp266.variant,
    }),
    miniQuizExp255: exp255.miniQuizExp255,
    guestFreemiumReengagementExp260:
      reengagement.guestFreemiumReengagementExp260,
    freemiumCheckoutConversionExp266:
      reengagement.freemiumCheckoutConversionExp266,
  };
}
